import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router'
import { PlatformService } from 'src/app/singleton-services/platform.service'
import { RouterService } from 'src/app/singleton-services/router.service'

export interface Redirect {
  testRegExp: RegExp
  newUrl: string
}

/**
 * Guard to redirect to a new url based on RegExp
 *
 * Provide a `Redirect` object to route data param to redirect to internal or
 * external urls based on provided testRegExp. External Urls open in a new tab.
 * Useful in support of multitenancy and experimentation in cases where certain
 * features should be redirected to alternate urls.
 *
 * TODO: add support for server side redirects. Currently redirects are
 * handled in fastly.
 */
@Injectable({
  providedIn: 'root'
})
export class RedirectGuard implements CanActivate {
  constructor(
    private readonly routerService: RouterService,
    private readonly platformService: PlatformService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  private get window() {
    return this.document.defaultView
  }

  public canActivate(activatedRouteSnapshot: ActivatedRouteSnapshot, routerStateSnapshot: RouterStateSnapshot) {
    const url = routerStateSnapshot.url
    const redirect: Redirect = activatedRouteSnapshot.data?.redirect
    if (!redirect || !redirect.newUrl || !redirect.testRegExp) {
      return true
    }
    const matchesRedirect = redirect.testRegExp.test(url)
    const newUrl = redirect.newUrl
    if (matchesRedirect) {
      const isExternalUrl = /^http(?:s)?:\/{2}\S+$/.test(newUrl)
      if (isExternalUrl) {
        if (this.platformService.isBrowser) {
          this.window?.open(newUrl)
        } else {
          return true
        }
      } else {
        this.routerService.router.navigateByUrl(newUrl)
      }
      return false
    } else {
      return true
    }
  }
}
